<!--
AssistantCreditTiles — franchise-facing credit usage tiles for the assistant dashboard.

TILE DATA = COST-FREE ONLY.
  - From useCreditWallet: balance_display (balance) + balanceMicro
  - From useCreditKpis (AiCredits_KpisResponse): rows[].total_micro, settle_count,
    topup_count, period.

LEAK-GUARD: NO cost / markup / margin / COGS / provider_cost / raw_cost / fx_* keys
are rendered here. The franchise kpis/wallet contracts are ISP-disjoint from the Admin
family (StaffCreditsKpiCards). This is enforced structurally (the contracts declare no
such fields) and tested with a leak-guard assertion in the integration spec.

Props:
  balance         — formatted balance string from useCreditWallet
  balanceMicro    — raw micro-credits from useCreditWallet
  kpis            — AiCredits_KpisResponseDados | null from useCreditKpis
  loading         — whether kpis are being fetched
-->
<template>
  <div class="assistant-credit-tiles" data-testid="assistant-credit-tiles">
    <!-- Balance tile -->
    <div class="kpi-card-wrapper">
      <div class="kpi-card balance-card">
        <div class="kpi-icon">
          <i class="pi pi-wallet"></i>
        </div>
        <div class="kpi-content">
          <h4>{{ t('Saldo de Créditos') }}</h4>
          <h2 data-testid="credit-tile-balance">{{ balance || '—' }}</h2>
          <span class="kpi-sub" data-testid="credit-tile-balance-micro">
            {{ formatCredits(balanceMicro) }} {{ t('cr') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Consumption tile (total_micro from latest kpis row, or sum) -->
    <div class="kpi-card-wrapper">
      <div class="kpi-card consumption-card">
        <div class="kpi-icon">
          <i class="pi pi-chart-bar"></i>
        </div>
        <div class="kpi-content">
          <h4>{{ t('Consumo no Período') }}</h4>
          <h2 data-testid="credit-tile-consumption">
            <template v-if="loading">{{ t('Carregando…') }}</template>
            <template v-else-if="kpis">
              {{ formatCredits(totalConsumptionMicro) }} {{ t('cr') }}
            </template>
            <template v-else>—</template>
          </h2>
          <span v-if="kpis" class="kpi-sub" data-testid="credit-tile-period">
            {{ kpis.period }}
          </span>
        </div>
      </div>
    </div>

    <!-- Settle count tile -->
    <div class="kpi-card-wrapper">
      <div class="kpi-card settle-card">
        <div class="kpi-icon">
          <i class="pi pi-check-circle"></i>
        </div>
        <div class="kpi-content">
          <h4>{{ t('Sessões Consumidas') }}</h4>
          <h2 data-testid="credit-tile-settle-count">
            <template v-if="loading">{{ t('Carregando…') }}</template>
            <template v-else-if="kpis">{{ totalSettleCount }}</template>
            <template v-else>—</template>
          </h2>
        </div>
      </div>
    </div>

    <!-- Topup count tile -->
    <div class="kpi-card-wrapper">
      <div class="kpi-card topup-card">
        <div class="kpi-icon">
          <i class="pi pi-plus-circle"></i>
        </div>
        <div class="kpi-content">
          <h4>{{ t('Recargas no Período') }}</h4>
          <h2 data-testid="credit-tile-topup-count">
            <template v-if="loading">{{ t('Carregando…') }}</template>
            <template v-else-if="kpis">{{ totalTopupCount }}</template>
            <template v-else>—</template>
          </h2>
        </div>
      </div>
    </div>

    <!-- Pending topups tile: only shown when there is an in-flight purchase.
         Flips to 'confirmado' once credit.topup.confirmed fires (status updated
         by useCreditPurchase.pollPurchaseStatus before emitting the event).
         Cleared when the parent resets pendingPurchase after confirmation. -->
    <div
      v-if="pendingPurchase"
      class="kpi-card-wrapper"
      data-testid="credit-tile-pending-topups"
    >
      <div
        class="kpi-card pending-card"
        :class="pendingPurchase.status === 'confirmed' ? 'pending-card--confirmed' : ''"
      >
        <div class="kpi-icon">
          <i
            class="pi"
            :class="pendingPurchase.status === 'confirmed' ? 'pi-check-circle' : 'pi-clock'"
          ></i>
        </div>
        <div class="kpi-content">
          <h4>{{ t('Recargas pendentes') }}</h4>
          <h2 data-testid="credit-tile-pending-status">
            <template v-if="pendingPurchase.status === 'confirmed'">
              {{ t('Confirmado') }}
            </template>
            <template v-else>
              {{ t('Aguardando confirmação') }}
            </template>
          </h2>
          <span class="kpi-sub" data-testid="credit-tile-pending-charge-id">
            {{ t('Cód.') }} {{ pendingPurchase.charge_id }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { AiCredits_KpisResponseDados } from '../../contracts/AiCredits_KpisResponse'
import type { PendingPurchase } from '../../composables/useCreditPurchase'
import { formatCredits } from './formatCredits'

const { t } = useI18n()

interface Props {
  balance: string
  balanceMicro: number
  kpis: AiCredits_KpisResponseDados | null
  loading: boolean
  /**
   * In-flight purchase from useCreditPurchase (F5/F7).
   * Rendered as a 'Recargas pendentes' tile when present.
   * Flips to 'confirmado' once credit.topup.confirmed fires
   * (useCreditPurchase.pollPurchaseStatus sets status='confirmed' before emitting).
   * Pass null when there is no pending purchase.
   */
  pendingPurchase?: PendingPurchase | null
}

const props = defineProps<Props>()

const totalConsumptionMicro = computed<number>(() => {
  if (!props.kpis) return 0
  return props.kpis.rows.reduce((sum, row) => sum + row.total_micro, 0)
})

const totalSettleCount = computed<number>(() => {
  if (!props.kpis) return 0
  return props.kpis.rows.reduce((sum, row) => sum + row.settle_count, 0)
})

const totalTopupCount = computed<number>(() => {
  if (!props.kpis) return 0
  return props.kpis.rows.reduce((sum, row) => sum + row.topup_count, 0)
})
</script>

<style scoped>
.assistant-credit-tiles {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.kpi-card-wrapper {
  flex: 1 1 160px;
  min-width: 140px;
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 0.5rem;
  background: var(--p-surface-50, #f8fafc);
  border: 1px solid var(--p-surface-200, #e2e8f0);
}

.kpi-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.balance-card .kpi-icon {
  background: var(--p-blue-100, #dbeafe);
  color: var(--p-blue-600, #2563eb);
}

.consumption-card .kpi-icon {
  background: var(--p-orange-100, #ffedd5);
  color: var(--p-orange-600, #ea580c);
}

.settle-card .kpi-icon {
  background: var(--p-green-100, #dcfce7);
  color: var(--p-green-600, #16a34a);
}

.topup-card .kpi-icon {
  background: var(--p-purple-100, #f3e8ff);
  color: var(--p-purple-600, #9333ea);
}

.pending-card .kpi-icon {
  background: var(--p-yellow-100, #fef9c3);
  color: var(--p-yellow-600, #ca8a04);
}

.pending-card--confirmed .kpi-icon {
  background: var(--p-green-100, #dcfce7);
  color: var(--p-green-600, #16a34a);
}

.kpi-content {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.kpi-content h4 {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--p-text-muted-color, #64748b);
  white-space: nowrap;
}

.kpi-content h2 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--p-text-color, #1e293b);
  white-space: nowrap;
}

.kpi-sub {
  font-size: 0.7rem;
  color: var(--p-text-muted-color, #64748b);
}
</style>
